import React from 'react';
import classNames from 'classnames';
import {
	Broadcast,
	BroadcastStatus,
	IN_PROGRESS_STATUSES,
	StreamStatus,
} from '../../../stores/types/broadcasting';
import { Probe, ProbeStatus } from '../types';
import HealthPanel from '../health-panel';
import Chip from '../chip';
import CopyValue from '../copy-value';
import StatusPill from '../status-pill';
import {
	formatAgo, formatDurationMs, formatElapsed, formatTimestamp, redactStreamUrl,
} from '../format';

interface Props {
	probe: Probe<Broadcast> | undefined;
}

/** Egress state transitions, newest first - the history LiveKit does not keep. */
function Timeline({ broadcast }: { broadcast?: Broadcast | null }) {
	const entries = broadcast?.timeline ?? [];
	if (!entries.length) return null;

	return (
		<>
			<h4 className='health-panel__title health-panel__title--section'>Egress timeline</h4>
			<table className='health-table'>
				<thead>
					<tr>
						<th>When</th>
						<th>Status</th>
						<th>Reason</th>
					</tr>
				</thead>
				<tbody>
					{[...entries].reverse().map((entry) => (
						<tr key={`${entry.at}-${entry.status}`}>
							<td className='health-mono'>{formatTimestamp(entry.at)}</td>
							<td>
								<span className={classNames('health-pill', {
									'health-pill--error': entry.status === BroadcastStatus.failed
										|| entry.status === BroadcastStatus.aborted,
									'health-pill--ok': entry.status === BroadcastStatus.active,
									'health-pill--idle': entry.status !== BroadcastStatus.failed
										&& entry.status !== BroadcastStatus.aborted
										&& entry.status !== BroadcastStatus.active,
								})}
								>
									{entry.status}
								</span>
							</td>
							<td>
								{entry.error && <div className='health-error-text'>{entry.error}</div>}
								{entry.details && <div className='health-subtle'>{entry.details}</div>}
								{!entry.error && !entry.details && <span className='health-empty'>—</span>}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</>
	);
}

const encodingLabel = (encoding: { [key: string]: any }): string => {
	if (encoding.preset !== undefined) return `preset ${encoding.preset}`;
	return [
		`${encoding.width}×${encoding.height}`,
		encoding.framerate ? `${encoding.framerate}fps` : '',
		encoding.videoBitrate ? `${encoding.videoBitrate}kbps video` : '',
		encoding.audioBitrate ? `${encoding.audioBitrate}kbps audio` : '',
	].filter(Boolean).join(' · ');
};

const streamStatusToProbeStatus = (status?: StreamStatus): ProbeStatus => {
	if (status === StreamStatus.failed) return ProbeStatus.Error;
	if (status === StreamStatus.active) return ProbeStatus.Ok;
	return ProbeStatus.Empty;
};

/**
 * Spielberg broadcast, which is a LiveKit web egress. Sync refreshes this entity
 * against Spielberg on read, so this is the egress service's own view rather than
 * a cached copy from whenever the broadcast started.
 *
 * No throughput or bitrate here on purpose: LiveKit exposes per-egress media
 * metrics only as Prometheus metrics on the egress service itself.
 *
 * Deliberately NOT here either: a stall detector. `EgressInfo.updated_at` only
 * moves on state changes (see `broadcasting.ts`), so a running egress cannot be
 * told from a wedged one through this API.
 */
export default function BroadcastPanel({ probe }: Props) {
	const broadcast = probe?.data;
	const outputs = broadcast?.outputs ?? [];
	const encoding = broadcast?.encodingOptions;
	const isRunning = !!broadcast?.status && IN_PROGRESS_STATUSES.includes(broadcast.status);
	const hasFailed = broadcast?.status === BroadcastStatus.failed
		|| broadcast?.status === BroadcastStatus.aborted
		|| broadcast?.status === BroadcastStatus.limitReached;

	const failedOutputs = outputs.filter((output) => output.status === StreamStatus.failed);

	// A destination that keeps reconnecting still reports `active` between drops,
	// so the retry count is the only thing that shows it flapping. This replaces
	// the old updatedAt staleness check, which flagged every healthy broadcast.
	const retryingOutputs = outputs.filter((output) => !!output.retries);
	const isDegraded = !!retryingOutputs.length || !!broadcast?.retryCount;

	/* eslint-disable-next-line no-nested-ternary */
	const statusPill = hasFailed
		? ProbeStatus.Error
		/* eslint-disable-next-line no-nested-ternary */
		: isDegraded
			? ProbeStatus.Degraded
			: isRunning ? ProbeStatus.Ok : ProbeStatus.Empty;

	return (
		<HealthPanel
			title='Broadcast egress'
			source='sync → spielberg'
			probe={probe}
		>
			{/* Failure reason first: it is the reason a producer opened this panel. */}
			{!!broadcast?.error && (
				<p className='health-banner health-banner--error'>
					{broadcast.error}
					{!!broadcast.errorCode && ` (code ${broadcast.errorCode})`}
				</p>
			)}
			{!!broadcast?.details && !broadcast?.error && (
				<p className='health-panel__note'>{broadcast.details}</p>
			)}
			{!!retryingOutputs.length && (
				<p className='health-banner health-banner--warn'>
					{retryingOutputs.length === 1 ? 'A destination has' : `${retryingOutputs.length} destinations have`}
					{' '}reconnected mid-broadcast. A retrying output reports
					{' '}<strong>active</strong> between drops, so the count is the only sign.
				</p>
			)}

			<div className='health-chips'>
				<StatusPill status={statusPill} label={broadcast?.status ?? 'unknown'} />
				{!!broadcast?.sourceType && <Chip label='source' value={broadcast.sourceType} />}
				{!!broadcast?.startTime && !broadcast?.endTime && (
					<Chip label='live for' value={formatElapsed(broadcast.startTime)} mono />
				)}
				{!!broadcast?.startTime && !!broadcast?.endTime && (
					<Chip label='ended' value={formatTimestamp(broadcast.endTime)} mono />
				)}
				{!broadcast?.startTime && (
					<Chip label='started' value={formatTimestamp(broadcast?.startTime)} mono />
				)}
				{!!encoding && <Chip label='encoding' value={encodingLabel(encoding)} mono />}
				<Chip label='provider' value={broadcast?.provider ?? '—'} />
				{!!broadcast?.retryCount && (
					<Chip label='restarted' value={`×${broadcast.retryCount}`} tone='warn' />
				)}
				{!!failedOutputs.length && !hasFailed && (
					<Chip
						label='destinations failed'
						value={`${failedOutputs.length} of ${outputs.length}`}
						tone='warn'
					/>
				)}
			</div>

			{/* The long identifiers, kept together and out of the chip row: they are
				what a producer copies into a ticket, not what they scan. */}
			<dl className='health-idents'>
				<dt>Egress</dt>
				<dd><CopyValue value={broadcast?.id ?? ''} mono /></dd>
				<dt>Capturing</dt>
				<dd><CopyValue value={broadcast?.sourceUrl ?? ''} mono /></dd>
				{!!broadcast?.roomName && (
					<>
						{/* Confirms the egress attached to the room the producer is in. */}
						<dt>Room</dt>
						<dd className='health-mono'>{broadcast.roomName}</dd>
					</>
				)}
			</dl>

			{/* "20m ago" on a two hour show is healthy - it is the age of the last
				transition, not evidence of a stall. */}
			<p className='health-subtle'>
				Last state change {broadcast?.updatedAt ? formatAgo(broadcast.updatedAt) : '—'}
				{' · egress transitions only, not a heartbeat'}
			</p>

			{!outputs.length ? (
				<p className='health-panel__note'>No RTMP outputs reported yet.</p>
			) : (
				<table className='health-table'>
					<thead>
						<tr>
							<th>Destination</th>
							<th>Status</th>
							<th>Streaming for</th>
							<th>Reconnects</th>
						</tr>
					</thead>
					<tbody>
						{outputs.map((output, index) => (
							// eslint-disable-next-line react/no-array-index-key
							<tr key={`${output.url}-${index}`}>
								<td className='health-mono'>{redactStreamUrl(output.url ?? '')}</td>
								<td>
									<StatusPill
										status={streamStatusToProbeStatus(output.status)}
										label={output.status ?? 'unknown'}
									/>
									{output.error && <div className='health-error-text'>{output.error}</div>}
								</td>
								<td className='health-mono'>
									{output.duration
										? formatDurationMs(output.duration)
										: formatTimestamp(output.startTime)}
								</td>
								{/* Three states: `undefined` means spielberg is not forwarding
									retry counts, which must not read as "no reconnects". */}
								<td>
									{output.retries === undefined && <span className='health-empty'>—</span>}
									{output.retries === 0 && <span className='health-empty'>none</span>}
									{!!output.retries && (
										<span className='health-tag health-tag--warn'>
											×{output.retries}
											{!!output.lastRetryAt && ` · last ${formatAgo(output.lastRetryAt)}`}
										</span>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}

			<Timeline broadcast={broadcast} />
		</HealthPanel>
	);
}
